import { getDownloadURL, ref } from "firebase/storage";
import React, { useEffect, useRef, useState } from "react";
import { storage } from "../firebase/fireInit";
import useHandlePlayerArrays from "../hooks/useHandlePlayerArrays";
import BackButton from "../misc/elements/BackButton";
import { forArrayLength } from "../misc/helperfuncs";
import collectionStore from "../stores/collectionStore";
import FillInComplete from "./FillInComplete";
import { getInputs } from "./inputs";
import { getNewObject } from "./newObjects";
import { makeOpenObject, makeSavedObject, saveObject } from "./saveObjects";
import { toast } from "react-toastify";
import CreateInputHolder from "./CreateInputHolder";
import useHandleRelated from "../hooks/useHandleRelated";
import useFirebaseSearch from "../hooks/useFirebaseSearch";
import FoundSimilars from "./FoundSimilars";
import useHandleBoxTypes from "../hooks/useHandleBoxTypes";

const Create = ({
  action,
  create,
  setCreate,
  player,
  onObjectCreated,
  width,
  height,
  marginLeft,
  onBack,
}) => {
  const [newObject, setNewObject] = useState(null);
  const [displayedInputs, setDisplayedInputs] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [filledOut, setFilledOut] = useState(false);
  const [clicks, setClicks] = useState(null);
  const [refs, setRefs] = useState([]);
  const [refIndex, setRefIndex] = useState(1);
  const { addObject } = useHandlePlayerArrays();
  const { addGeneralObjects } = collectionStore();
  const [saving, setSaving] = useState(false);
  const handleRelated = useHandleRelated({ object: newObject });
  const fireSearchRef = useRef(null);
  const handleBoxTypes = useHandleBoxTypes();
  const { getBoxType } = useHandleBoxTypes();

  function onSearched(results) {
    setSearchResults(results);
  }

  useFirebaseSearch({
    searchInput: refs[0]?.current.value ?? "",
    type: "all",
    onSearched,
    pauseInterval: 10,
    includeCategories: false,
    searching: true,
  });

  // handle related objects

  useEffect(() => {
    if (saving) save();
  }, [saving]);

  function saveObjectLocally(object) {
    if (action == "Create") {
      addObject("open", makeOpenObject(object, create.type));
      addObject("saved", makeSavedObject(object, getBoxType(create.type)));
      if (onObjectCreated) onObjectCreated(object);
      setCreate(null);
    } else if (action == "Open") {
      const openObject = makeOpenObject(object, create.type);
      addObject("open", openObject);
      if (onObjectCreated) onObjectCreated(openObject);
      setCreate(null);
    }
  }

  function save() {
    if (action == "Create") {
      saveObject(
        newObject,
        create.type,
        player,
        saveObjectLocally,
        addGeneralObjects,
        handleBoxTypes
      );
    }
    if (action == "Open") {
      saveObjectLocally(newObject);
    }
    handleRelated.addReferenceToRelatedObjects();

    setSaving(false);
  }

  useEffect(() => {
    if (newObject) {
      var filled = true;

      for (let i = 0; i < mandatoryFields.length; i++) {
        const field = mandatoryFields[i];
        if (field.type == "string" || field.type == "int") {
          filled =
            newObject[field.field]?.length >= field.minLength &&
            newObject[field.field]?.length <= field.maxLength;
          if (!filled) break;
        } else if (field.type == "array") {
          filled = newObject[field.field].length >= field.minLength;
          if (!filled) break;
        }
      }
      setFilledOut(filled);
    }
  }, [newObject]);

  function parseMandatoryFields(inputs) {
    const fields = [];
    forArrayLength(inputs, (input) => {
      if (input.mandatory) {
        const field = {
          field: input.field,
          type: input.inputType,
          minLength: input.payload.minLength,
          maxLength: input.payload.maxLength ?? 9999,
        };
        fields.push(field);
      }
    });

    setMandatoryFields(fields);
  }

  useEffect(() => {
    if (create) {
      const newObj = {
        ...getNewObject(player, create.type, handleBoxTypes.getBoxType),
        shortDescription: create.payload?.shortDescription ?? "",
        longDescription: create.payload?.longDescription ?? "",
        id: create.payload?.shortDescription
          .replace(/[^a-z]/gi, "")
          .toLowerCase(),
        image: create.payload?.image ?? null,
        categories: create.payload?.categories ?? [],
        keywords: create.payload?.keywords ?? [],
        things: create.payload?.things ?? [],
      };
      const inputs = getInputs(
        create.type,
        newObj,
        create.extras,
        handleBoxTypes.getBoxType(newObj.type)
      );
      parseMandatoryFields(inputs);
      setNewObject(newObj);
    }
  }, []);

  useEffect(() => {
    if (newObject) {
      const id = newObject.shortDescription
        .replace(/[^a-z]/gi, "")
        .toLowerCase();
      setNewObject({ ...newObject, id });
    }
  }, [newObject?.shortDescription, newObject?.image, newObject?.keywords]);

  useEffect(() => {
    if (refs.length > 0) {
      refs[0].current.focus();
    }
  }, [refs]);

  function addInputRef(inputRef) {
    setRefs((state) => {
      if (!state.includes(inputRef)) return [...state, inputRef];
      else return state;
    });
  }

  function onEnter(e) {
    if (e.key == "Enter") {
      refs[refIndex].current.focus();
      setRefIndex((state) => (state + 1) % refs.length);
    }
  }

  function onImageChanged(path, field) {
    getDownloadURL(ref(storage, path)).then((url) => {
      setNewObject({ ...newObject, [field]: url });
    });
  }

  function onRelatedObjectClicked(object, field) {
    const alreadyThere =
      newObject[field].find((o) => o.id == object.id) != null;
    if (!alreadyThere)
      setNewObject({ ...newObject, [field]: [...newObject[field], object] });
  }

  function removeObject(object, field) {
    setRemovedObject({ object, field });
    setNewObject({
      ...newObject,
      [field]: newObject[field].filter((o) => o.id != object.id),
    });
  }

  function addKeyword(newKeyword) {
    if (newKeyword.length > 3) {
      if (!newObject.keywords.includes(newKeyword.toLocaleLowerCase())) {
        setNewObject({
          ...newObject,
          keywords: [...newObject.keywords, newKeyword.toLocaleLowerCase()],
        });
      }
    } else toast("Keyword has to be at least 4 characters long");
  }

  function removeKeyword(keyword) {
    setNewObject({
      ...newObject,
      keywords: newObject.keywords.filter((k) => k != keyword),
    });
  }

  function onItemPicked(item, list) {
    if (newObject[list].includes(item))
      setNewObject({
        ...newObject,
        [list]: newObject[list].filter((c) => c != item),
      });
    else setNewObject({ ...newObject, [list]: [...newObject[list], item] });
  }

  function removePicklistItem(item, list) {
    setNewObject({
      ...newObject,
      [list]: newObject[list].filter((c) => c != item),
    });
  }

  function onTimeEntered(totalMinutes, field) {
    if (newObject) {
      setNewObject({ ...newObject, [field]: totalMinutes });
    }
  }

  function rateClick(rating, field) {
    const newRatings = { ...newObject.ratings };
    newRatings[field].rating = rating;
    setNewObject({ ...newObject, ratings: newRatings });
  }

  function onInputChange(value, field) {
    setNewObject({
      ...newObject,
      [field]: value,
    });
  }

  const [removedObject, setRemovedObject] = useState(null);

  useEffect(() => {
    const obj = {};
    obj.onImageChanged = onImageChanged;
    obj.onRelatedObjectClicked = onRelatedObjectClicked;
    obj.removeObject = removeObject;
    obj.addKeyword = addKeyword;
    obj.removeKeyword = removeKeyword;
    obj.onItemPicked = onItemPicked;
    obj.removePicklistItem = removePicklistItem;
    obj.onTimeEntered = onTimeEntered;
    obj.onEnter = onEnter;
    obj.onInputChange = onInputChange;
    obj.rateClick = rateClick;
    obj.removedObject = removedObject;
    setClicks(obj);
  }, [newObject]);

  // create inputs for goal
  return (
    <div
      className="divColumn"
      style={{
        marginLeft,
        width,
        height,
        backgroundColor: "#e1e1e1",
        borderRadius: "1rem/1rem",
        position: "relative",
      }}
    >
      <div
        className="divRow"
        style={{ position: "absolute", left: 0, marginLeft: "5px", top: 2 }}
      >
        <BackButton
          onClick={() => {
            if (onBack) onBack();
            setCreate(null);
          }}
        />
      </div>
      <CreationHeader
        type={create.type}
        newObject={newObject}
        action={action}
      />
      <div
        className="divColumn"
        style={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        {newObject &&
          getInputs(
            create.type,
            newObject,
            create.extras,
            handleBoxTypes.getBoxType(newObject.type)
          ).map((input, index) => {
            return (
              <CreateInputHolder
                action={action}
                key={index}
                addInputRef={addInputRef}
                clicks={clicks}
                type={input.type}
                name={input.name}
                field={input.field}
                hint={input.hint}
                extras={input.extras}
                mandatory={input.mandatory}
                payload={input.payload}
              />
            );
          })}
        {filledOut && (
          <FillInComplete
            type={create.type}
            onClick={() => setSaving(true)}
            action={action}
          />
        )}
      </div>
      <FoundSimilars
        results={searchResults}
        type={handleBoxTypes.getPluralType(create.type)}
      />
    </div>
  );
};

const CreationHeader = ({ type, newObject, action }) => {
  return (
    <div
      className="divRow"
      style={{ width: "50%", justifyContent: "center" }}
      onClick={() => {}}
    >
      <div
        className="textBoldWhite"
        style={{ color: "gray", fontSize: "30px" }}
      >{`${action} ${type.toUpperCase()}`}</div>
      <img className="icon25" src={`/icons/icon_${type}.png`} />
    </div>
  );
};

export default Create;
